import Box from '@mui/material/Box'
import { useState, useEffect, useCallback } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import PaymentMethod from '@app/components/EFTPaymentMethod/PaymentMethod'
import { createCustomerAttachPayment } from '@app/firebase/functions'
import { useCurrentUser } from '@app/context/CurrentUser'
import { collection } from '@app/firebase/firestore'
import { CircularProgress, Typography } from '@mui/material'

const VITE_STRIPE_KEY = import.meta.env.VITE_STRIPE_KEY
const stripePromise = loadStripe(VITE_STRIPE_KEY)

/**
 * @typedef {Object} StripeInfo
 * @property {string} customerId - The Stripe customer ID
 * @property {string} email - The customer's email
 * @property {string} name - The customer's name
 * @property {Object} setupIntentInfo - Information about the setup intent
 */

/**
 * EFTEnrollment component for managing electronic funds transfer enrollment through Stripe payment
 * processing.
 *
 * @returns {JSX.Element} The EFT enrollment component
 */
const EFTEnrollment = () => {
  const [clientSecret, setClientSecret] = useState(null)
  const { profileData } = useCurrentUser()
  const [pageDataLoading, setPageDataLoading] = useState(false)
  const [existingCustomerId, setExistingCustomerId] = useState(null)
  const [existingCustomerDetails, setExistingCustomerDetails] = useState(null)
  const [existingSetupIntentInfo, setExistingSetupIntentInfo] = useState(null)

  /**
   * Fetches plan administrator data from Firestore and sets up the Stripe payment process Memoized
   * to prevent recreation between renders
   *
   * @async
   * @returns {Promise<void>}
   */
  const getPlanAdminData = useCallback(async () => {
    collection('planAdmins')
      .where('adminIDs', 'array-contains', profileData.id)
      .limit(1)
      .onSnapshot((snapshot) => {
        if (snapshot.empty) {
          setPageDataLoading(false)
          console.error('** No planAdmin document!')
          return
        }

        snapshot.forEach(async (doc) => {
          if (!doc.exists) {
            setPageDataLoading(false)
            console.error('** No planAdmin document!')
            return null
          }

          const { stripe } = doc.data()
          const fetchExistingCustomerId = stripe?.customerId || null

          setExistingCustomerId(fetchExistingCustomerId)
          setExistingCustomerDetails({
            email: stripe?.email || '',
            name: stripe?.name || '',
          })
          setExistingSetupIntentInfo(stripe?.setupIntentInfo || null)

          try {
            const data = await createCustomerAttachPayment({
              email: profileData.email,
              customerId: fetchExistingCustomerId,
            })

            setClientSecret(data.clientSecret)
            setPageDataLoading(false)
          } catch (err) {
            setPageDataLoading(false)
            console.error('Error creating customer payment:', err)
          }
        })
      })
  }, [profileData?.id, profileData?.email])

  useEffect(() => {
    if (!profileData?.id) return

    setPageDataLoading(true)
    getPlanAdminData()
  }, [profileData?.id, getPlanAdminData])

  // Stripe appearance configuration
  const appearance = {
    theme: 'stripe',
  }

  const loader = 'auto'

  return (
    <>
      {clientSecret && (
        <Elements
          options={{
            clientSecret,
            appearance,
            loader,
          }}
          stripe={stripePromise}
        >
          <PaymentMethod
            existingSetupIntentInfo={existingSetupIntentInfo}
            existingCustomerId={existingCustomerId}
            setExistingSetupIntentInfo={setExistingSetupIntentInfo}
            existingCustomerDetails={existingCustomerDetails}
          />
        </Elements>
      )}
      {pageDataLoading && (
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          minHeight='200px'
          gap={2}
        >
          <CircularProgress />
          <Typography variant='body1' color='text.secondary'>
            Initializing your EFT environment...
          </Typography>
        </Box>
      )}
    </>
  )
}

export default EFTEnrollment
